import { Multimap } from "@flakiness/shared/common/multimap.js";
import { WireTypes } from "../wireTypes.js";
import { Extractor, outcomeToStatusFilter } from "./extractor.js";
import { Operator } from "./operators.js";
import { Primitive } from "./primitive.js";
import { Token } from "./tokens.js";

type MatcherSourceInfo = {
  first: Token;
  last: Token;
  prefix: Token[],
  valuesTokens: Token[][];
};

export enum MatchResult {
  MATCH,
  NO_MATCH,
  EXCLUDE,
}

export namespace MatchResult {
  export function and(a: MatchResult, b: MatchResult): MatchResult {
    if (a === MatchResult.EXCLUDE || b === MatchResult.EXCLUDE) return MatchResult.EXCLUDE;
    if (a === MatchResult.NO_MATCH || b === MatchResult.NO_MATCH) return MatchResult.NO_MATCH;
    return MatchResult.MATCH;
  }
}

class MatcherFactory<F, T> {
  public readonly prefix: string;
  public readonly aliases: Token[][];
  constructor(prefixes: string[], public readonly extractor: Extractor<F, T>, public readonly op: Operator<T>, public readonly primitive: Primitive<T>) {
    this.prefix = prefixes[0];
    this.aliases = prefixes.map(prefix => Token.tokenize(prefix).tokens);
  }

  create(values: T[]) {
    return new Matcher(this.prefix, this.primitive, this.extractor, this.op, values, false);
  }

  parse(tokens: Token[], tokenIndex: number): { matcher: Matcher<F, T>, newTokenIndex: number }|undefined {
    const isNegation = tokens[tokenIndex].type === 'punctuation' && tokens[tokenIndex].value === '-';
    if (isNegation)
      ++tokenIndex;

    for (const alias of this.aliases) {
      if (alias.length + tokenIndex > tokens.length)
        continue;
      if (!alias.every((token, idx) => tokens[tokenIndex + idx].isEqual(token)))
        continue;
      const rawValues = parseValueList(tokens, tokenIndex + alias.length);
      if (!rawValues)
        continue;
      const values = rawValues.values.map(v => this.primitive.parse(v));
      if (!values.every(x => x !== undefined))
        continue;
      return {
        matcher: new Matcher(this.prefix, this.primitive, this.extractor, this.op, values, isNegation, {
          first: tokens[tokenIndex],
          last: tokens[rawValues.newTokenIndex - 1],
          prefix: alias,
          valuesTokens: rawValues.valueTokens,
        }),
        newTokenIndex: rawValues.newTokenIndex,
      };
    }
    return undefined;
  }
}

export class Matcher<F, T> {
  static TAG_CONTAINS = new MatcherFactory(['#'], Extractor.TAG, Operator.stringOperators.CONTAINS, Primitive.STRING);
  static ANNOTATION_CONTAINS = new MatcherFactory(['@'], Extractor.ANNOTATION_PARTS, Operator.stringOperators.CONTAINS, Primitive.STRING);
  static TEST_ID_EQ = new MatcherFactory(['test.id='], Extractor.TEST_ID, Operator.stringOperators.EQ, Primitive.STRING);
  static TEST_FILE_CONTAINS = new MatcherFactory(['file:', 'f:'], Extractor.TEST_FILE, Operator.stringOperators.CONTAINS, Primitive.STRING);
  static STATUS_CONTAINS = new MatcherFactory(['status:', 's:'], Extractor.STATUS, Operator.stringOperators.CONTAINS, Primitive.STRING);
  static ERR_CONTAINS = new MatcherFactory(['$'], Extractor.ERROR_TEXT, Operator.stringOperators.CONTAINS, Primitive.STRING);
  static TEST_LN_EQ = new MatcherFactory([':'], Extractor.TEST_LINE, Operator.numberOperators.EQ, Primitive.INTEGER);
  static TEST_LN_LT = new MatcherFactory(['<'], Extractor.TEST_LINE, Operator.numberOperators.LT, Primitive.INTEGER);
  static TEST_LN_GT = new MatcherFactory(['>'], Extractor.TEST_LINE, Operator.numberOperators.GT, Primitive.INTEGER);
  static TEST_LN_LTE = new MatcherFactory(['<='], Extractor.TEST_LINE, Operator.numberOperators.LTE, Primitive.INTEGER);
  static TEST_LN_GTE = new MatcherFactory(['>='], Extractor.TEST_LINE, Operator.numberOperators.GTE, Primitive.INTEGER);
  static TEST_DURATION_GT = new MatcherFactory(['duration>', 'd>'], Extractor.TEST_DURATION, Operator.numberOperators.GT, Primitive.DURATION);
  static TEST_DURATION_LT = new MatcherFactory(['duration<', 'd<'], Extractor.TEST_DURATION, Operator.numberOperators.LT, Primitive.DURATION);
  static TEST_DURATION_GTE = new MatcherFactory(['duration>=', 'd>='], Extractor.TEST_DURATION, Operator.numberOperators.GTE, Primitive.DURATION);
  static TEST_DURATION_LTE = new MatcherFactory(['duration<=', 'd<='], Extractor.TEST_DURATION, Operator.numberOperators.LTE, Primitive.DURATION);
  static FLIP_RATE_GT = new MatcherFactory(['flip>', 'fr>'], Extractor.FLIP_RATE, Operator.numberOperators.GT, Primitive.PERCENTAGE);
  static FLIP_RATE_LT = new MatcherFactory(['flip<', 'fr<'], Extractor.FLIP_RATE, Operator.numberOperators.LT, Primitive.PERCENTAGE);
  static FLIP_RATE_GTE = new MatcherFactory(['flip>=', 'fr>='], Extractor.FLIP_RATE, Operator.numberOperators.GTE, Primitive.PERCENTAGE);
  static FLIP_RATE_LTE = new MatcherFactory(['flip<=', 'fr<='], Extractor.FLIP_RATE, Operator.numberOperators.LTE, Primitive.PERCENTAGE);
  static FAIL_RATE_GT = new MatcherFactory(['fail>'], Extractor.FAIL_RATE, Operator.numberOperators.GT, Primitive.PERCENTAGE);
  static FAIL_RATE_LT = new MatcherFactory(['fail<'], Extractor.FAIL_RATE, Operator.numberOperators.LT, Primitive.PERCENTAGE);
  static FAIL_RATE_GTE = new MatcherFactory(['fail>='], Extractor.FAIL_RATE, Operator.numberOperators.GTE, Primitive.PERCENTAGE);
  static FAIL_RATE_LTE = new MatcherFactory(['fail<='], Extractor.FAIL_RATE, Operator.numberOperators.LTE, Primitive.PERCENTAGE);
  static TEST_CONTAINS = new MatcherFactory([''], Extractor.TEST_TEXT_PARTS, Operator.stringOperators.CONTAINS, Primitive.STRING);

  static parse(tokens: Token[], tokenIndex: number): { matcher: Matcher<any, any>, newTokenIndex: number }|undefined {
    return Matcher.TAG_CONTAINS.parse(tokens, tokenIndex) ??
      Matcher.ANNOTATION_CONTAINS.parse(tokens, tokenIndex) ??
      Matcher.TEST_ID_EQ.parse(tokens, tokenIndex) ?? 
      Matcher.TEST_FILE_CONTAINS.parse(tokens, tokenIndex) ??
      Matcher.STATUS_CONTAINS.parse(tokens, tokenIndex) ??
      Matcher.ERR_CONTAINS.parse(tokens, tokenIndex) ??
      Matcher.TEST_LN_EQ.parse(tokens, tokenIndex) ??
      Matcher.TEST_LN_LT.parse(tokens, tokenIndex) ??
      Matcher.TEST_LN_GT.parse(tokens, tokenIndex) ??
      Matcher.TEST_LN_LTE.parse(tokens, tokenIndex) ??
      Matcher.TEST_LN_GTE.parse(tokens, tokenIndex) ??
      Matcher.TEST_DURATION_GT.parse(tokens, tokenIndex) ??
      Matcher.TEST_DURATION_LT.parse(tokens, tokenIndex) ??
      Matcher.TEST_DURATION_GTE.parse(tokens, tokenIndex) ??
      Matcher.TEST_DURATION_LTE.parse(tokens, tokenIndex) ??
      Matcher.FLIP_RATE_GT.parse(tokens, tokenIndex) ??
      Matcher.FLIP_RATE_LT.parse(tokens, tokenIndex) ??
      Matcher.FLIP_RATE_GTE.parse(tokens, tokenIndex) ??
      Matcher.FLIP_RATE_LTE.parse(tokens, tokenIndex) ??
      Matcher.FAIL_RATE_GT.parse(tokens, tokenIndex) ??
      Matcher.FAIL_RATE_LT.parse(tokens, tokenIndex) ??
      Matcher.FAIL_RATE_GTE.parse(tokens, tokenIndex) ??
      Matcher.FAIL_RATE_LTE.parse(tokens, tokenIndex) ??
      Matcher.TEST_CONTAINS.parse(tokens, tokenIndex);
  }

  static outcomes: Record<WireTypes.Outcome, Matcher<WireTypes.Outcome, string>>;

  static createErrorMatcher(text: string) {
    return Matcher.ERR_CONTAINS.create([text]);
  }

  static compare<K, V>(a: Matcher<K, V>, b: Matcher<K, V>): number {
    if (a.sourceInfo && b.sourceInfo)
      return a.sourceInfo.first.from - b.sourceInfo.first.from;
    if (a.prefix !== b.prefix)
      return a.prefix < b.prefix ? -1 : 1;
    if (a.values.length !== b.values.length)
      return a.values.length - b.values.length;
    for (let i = 0; i < a.values.length; ++i) {
      const result = a.primitive.cmp(a.values[i], b.values[i]);
      if (result !== 0)
        return result;
    }
    return 0;
  }

  public values: T[];

  constructor(
    public prefix: string,
    public primitive: Primitive<T>,
    public extractor: Extractor<F, T>,
    public op: Operator<T>,
    values: Iterable<T>,
    public exclusion: boolean,
    public sourceInfo?: MatcherSourceInfo,
  ) {
    this.values = Array.from(values);
  }

  match(e: F): MatchResult {
    const extracted = this.extractor.extract(e);
    const result = extracted.some(e => this.values.some(value => this.op.evaluate(e, value)));
    if (!result)
      return this.exclusion ? MatchResult.MATCH : MatchResult.NO_MATCH;
    return this.exclusion ? MatchResult.EXCLUDE : MatchResult.MATCH;
  }

  highlight(e: F, highlight: Multimap<T, [number, number]>): boolean {
    const extracted = this.extractor.extract(e);
    let result = extracted.some(e => this.values.some(value => this.op.evaluate(e, value)));
    if (this.exclusion && result)
      result = !result;
    if (result && highlight && extracted.length) {
      for (const e of extracted) {
        for (const value of this.values)
          this.op.highlight(e, value, highlight);
      }
    }
    return result;
  }

  clone(): Matcher<F, T> {
    return new Matcher<F, T>(this.prefix, this.primitive, this.extractor, this.op, this.values, this.exclusion, structuredClone(this.sourceInfo));
  }

  normalize(): Matcher<F, T> {
    // Just dropping the source info
    return new Matcher<F, T>(this.prefix, this.primitive, this.extractor, this.op, this.values.toSorted((a, b) => this.primitive.cmp(a, b)), this.exclusion);
  }

  serialize(): string {
    if (this.sourceInfo) {
      return [
        this.exclusion ? '-' : '',
        serializeTokens(this.sourceInfo.prefix),
        serializeValueTokens(this.sourceInfo.valuesTokens),
      ].join('');
    }
    const words = this.values.map(value => this.primitive.serialize(value)).join(', ');
    return [
      this.exclusion ? '-' : '',
      this.prefix,
      this.values.length > 1 ? `(${words})` : words,
    ].join('');
  }
}

Matcher.outcomes = {
  expected: Matcher.STATUS_CONTAINS.create([outcomeToStatusFilter('expected')]),
  unexpected: Matcher.STATUS_CONTAINS.create([outcomeToStatusFilter('unexpected')]),
  skipped: Matcher.STATUS_CONTAINS.create([outcomeToStatusFilter('skipped')]),
  flaked: Matcher.STATUS_CONTAINS.create([outcomeToStatusFilter('flaked')]),
  regressed: Matcher.STATUS_CONTAINS.create([outcomeToStatusFilter('regressed')]),
}

function serializeValueTokens(tokens: Token[][]) {
  if (tokens.length === 0)
    return '()';
  if (tokens.length === 1)
    return serializeTokens(tokens[0]);
  return `(${tokens.map(valueTokens => serializeTokens(valueTokens)).join(', ')})`;
}

export function parseValueList(tokens: Token[], tokenIndex: number): { values: string[], valueTokens: Token[][], newTokenIndex: number } | undefined {
  if (tokens[tokenIndex]?.isWord())
    return { values: [tokens[tokenIndex].value], valueTokens: [[tokens[tokenIndex]]], newTokenIndex: tokenIndex + 1 };

  if (!tokens[tokenIndex]?.isPunctuation('('))
    return undefined;
  ++tokenIndex;
  const values: string[] = [];
  const valueTokens: Token[][] = [];
  while (tokenIndex < tokens.length) {
    if (!tokens[tokenIndex]?.isWord())
      return undefined;
    valueTokens.push([tokens[tokenIndex]]);
    values.push(tokens[tokenIndex].value);
    ++tokenIndex;
    if (tokens[tokenIndex]?.isPunctuation(')'))
      return { values, valueTokens, newTokenIndex: tokenIndex + 1 };
    if (tokens[tokenIndex]?.isPunctuation(',')) {
      ++tokenIndex;
      continue;
    }
    return undefined;
  }
  return undefined;
}

function serializeTokens(tokens: Token[]) {
  if (!tokens.length)
    return '';
  let result = [];
  let lastToken: Token|undefined;
  for (const token of tokens) {
    if (lastToken)
      result.push(' '.repeat(token.from - lastToken.to))
    result.push(token.source.substring(token.from, token.to));
    lastToken = token;
  }
  return result.join('');
}
